import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { carByVinRequest } from "../../../../utils/requests/laximo/carByVinRequest";
import { checkByRegExp } from "./checkByRegExp";
import { carByRegistrationNumberRequest } from "../../../../utils/requests/laximo/carByRegistrationNumberRequest";
import { Warning } from "../../../../ui/Modal/Warning/Warning";
import { CarByVin } from "../../../../types/Car/CarByVin.interface";
import { useDispatch } from "react-redux";
import { selectCar } from "../../../../utils/storage/slices/userSlice";

/**
 * Выбор автомобиля по VIN или гос. номеру
 * @returns 
 */
export const SelectCarByVin = ({ setIsLoading }: { setIsLoading: Dispatch<SetStateAction<boolean>> }) => {
    const dispatch = useDispatch();
    const [line, setLine] = useState<string>('');
    const [error, setError] = useState<string | null>(null);
    const [isShownWarning, setIsShownWarning] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const lineRef = useRef<string>(line);

    /** Разбиваем гос. номер на номер и регион перед запросом */
    const splitCarNumberBeforeRequest = (carNumber: string) => {
        const numberWithoutSpaces = carNumber.replace(/\s/g, '');
        const number = numberWithoutSpaces.slice(0, 6).toUpperCase();
        let region = numberWithoutSpaces.slice(6);

        //Добавляем RUS, если этого нет
        if (region.length < 4) {
            region += 'RUS';
        }

        return carByRegistrationNumberRequest(number, region);
    }

    /** Запросы */
    const requests = {
        vin: carByVinRequest,
        carNumber: splitCarNumberBeforeRequest,
        bodyNumber: carByVinRequest,
    };

    /** Инициализация перехватчиков */
    useEffect(() => {
        //Перехват нажатия Enter
        const enterHandler = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                handleSearch(lineRef.current);
                inputRef.current?.blur();
            }
        };

        inputRef.current?.addEventListener('keydown', enterHandler);

        return () => inputRef.current?.removeEventListener('keydown', enterHandler);
    }, []);

    /** Запрос автомобиля по VIN */
    const handleSearch = (inputLine: string) => {
        const requestType = checkByRegExp(inputLine);

        //Устанавливаем ошибку, если введен неверный формат
        if (!requestType) {
            setError('Неверный формат ввода');
            setIsShownWarning(true);
            return;
        };

        const request = requests[requestType as keyof typeof requests];

        setIsLoading(true);
        request(inputLine)
            .then((data: CarByVin) => {
                const car = {
                    brandId: data.vehicle.catalog,
                    ssd: data.vehicle.ssd,
                    brand: data.vehicle.brand,
                    model: data.vehicle.name,
                    year: data.headers.find((header: { key: string, value: string }) => header.key === 'date')?.value?.split('.')?.[1] ?? '',
                    body: data.headers.find((header: { key: string, value: string }) => header.key === 'model')?.value ?? '',
                    engine: data.headers.find((header: { key: string, value: string }) => header.key === 'options')?.value.split(';')
                        .find((option: string) => option.split(':')?.[0].trim() === 'Двигатель')?.split(':')?.[1].trim() ?? '',
                };

                dispatch(selectCar(car));
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    /** Перехват ввода текста */
    const handleWriteText = (e: React.ChangeEvent<HTMLInputElement>) => {
        //Сбрасываем ошибку при вводе текста
        if (error) {
            setError(null);
        }

        lineRef.current = e.target.value;
        setLine(e.target.value);
    };

    return (
        <div className="flex-1 lg:max-w-md flex flex-col">
            <p className="text-sm mb-3 text-gray-600 font-medium">Подбор по VIN или гос. номеру</p>
            <div className="flex flex-col sm:flex-row gap-2 relative">
                <input type="text"
                    ref={inputRef}
                    placeholder="Введите VIN или гос. номер..."
                    value={line}
                    onChange={handleWriteText}
                    className={`bs-bb h-10 w-full rounded-md border border-input bg-background px-3 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm placeholder-gray-500${!!error ? ' border-red-400' : ''}`} />
                <button disabled={line.trim().length === 0 || !!error}
                    className="whitespace-nowrap text-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-10 bg-orange-500 hover:bg-orange-600 text-white px-6"
                    onClick={() => handleSearch(line)}>
                    Найти
                </button>
                <Warning message={error || ''} isShown={isShownWarning} setIsShow={setIsShownWarning} timeout={3000} />
            </div>
            <p className="text-xs mt-1 text-gray-600">Для максимально точного подбора выберите автомобиль по VIN (Идентификационный номер транспортного средства).</p>
        </div>
    );
};